import { useEffect, useRef } from "react";
import { hasGoneIdle } from "./kiosk";

/** Anything a person does to a tablet counts as "still here". Touch first — this runs on a wall device. */
const ACTIVITY_EVENTS = ["touchstart", "pointerdown", "keydown", "wheel", "scroll"] as const;

export interface IdleLockOptions {
  /** False switches the whole thing off — no listeners, no timer. */
  enabled: boolean;
  /** How long without activity before `onIdle` fires. Comes resolved from the server, so the hook never
   * has to know about the definition/instance inheritance. */
  timeoutSeconds: number;
  onIdle: () => void;
}

/**
 * Fires `onIdle` after a stretch of no interaction (KEY-268) — the trigger that drops a shared device to
 * its lock screen.
 *
 * A wall-mounted tablet is normally left alone rather than switched off, so the timer is deliberately
 * wall-clock-based rather than a plain `setTimeout` chain: a backgrounded or slept tab has its timers
 * throttled or frozen, and a device woken after two hours must be found idle immediately, not given a fresh
 * countdown. It therefore records the last activity timestamp and re-checks it on a short poll and on every
 * wake — the same `visibilitychange`/`focus` pattern the admin app already uses to revalidate a session.
 */
export function useIdleLock({ enabled, timeoutSeconds, onIdle }: IdleLockOptions): void {
  // Held in a ref so a re-render (of which the workspace has many) never restarts the countdown.
  const lastActivity = useRef(Date.now());
  // Kept in a ref so the effect doesn't re-subscribe every render just because the callback identity moved.
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;
  const fired = useRef(false);

  useEffect(() => {
    if (!enabled || timeoutSeconds <= 0) {
      return;
    }

    fired.current = false;
    lastActivity.current = Date.now();

    const note = () => {
      lastActivity.current = Date.now();
    };

    const check = () => {
      if (fired.current) return;
      if (hasGoneIdle(lastActivity.current, Date.now(), timeoutSeconds)) {
        fired.current = true;
        onIdleRef.current();
      }
    };

    // Coming back to a tab is itself activity — the person is at the device — but the elapsed time is
    // checked first, so a tablet woken long after the window expired locks rather than resetting.
    const onWake = () => {
      check();
      if (!fired.current) note();
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, note, { passive: true });
    }
    window.addEventListener("focus", onWake);
    document.addEventListener("visibilitychange", onWake);

    // A second is plenty: the timeout floor is 30s, and this is a timestamp comparison, not real work.
    const poll = window.setInterval(check, 1000);

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, note);
      }
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onWake);
      window.clearInterval(poll);
    };
  }, [enabled, timeoutSeconds]);
}
